import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Testimonials = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const vendors = [
    {
      name: 'Proxmox',
      logo: 'ri-server-line',
      description: 'Open-source virtualization platform',
      color: 'from-orange-500 to-red-500'
    },
    {
      name: 'ElasticSearch',
      logo: 'ri-search-line',
      description: 'Distributed search and analytics',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      name: 'Palo Alto Networks',
      logo: 'ri-shield-check-line',
      description: 'Next-generation firewall solutions',
      color: 'from-red-500 to-pink-500'
    },
    {
      name: 'Fortigate',
      logo: 'ri-lock-line',
      description: 'Network security appliances',
      color: 'from-red-600 to-orange-600'
    },
    {
      name: 'Unifi Network',
      logo: 'ri-router-line',
      description: 'Enterprise networking solutions',
      color: 'from-sky-500 to-blue-600'
    },
    {
      name: 'TrueNAS',
      logo: 'ri-database-2-line',
      description: 'Enterprise storage platform',
      color: 'from-blue-500 to-indigo-500'
    }
  ];

  return (
    <section id="testimonials" ref={ref} className="py-32 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gray-50 border border-gray-200 rounded-full mb-6">
            <i className="ri-team-line text-teal-600"></i>
            <span className="text-sm font-medium text-gray-700">Technology Partners</span>
          </div>
          <h2 className="text-5xl lg:text-6xl font-display font-bold text-gray-900 leading-tight mb-6">
            Trusted Vendor
            <br />
            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              Partnerships
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We work with industry-leading technology vendors to deliver best-in-class infrastructure solutions
          </p>
        </motion.div>

        {/* Vendor Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20"
        >
          {vendors.map((vendor, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="group relative"
            >
              <div className="relative bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-teal-500 transition-all duration-300 hover:shadow-xl">
                {/* Icon */}
                <div className={`w-16 h-16 flex items-center justify-center bg-gradient-to-br ${vendor.color} rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${vendor.logo} text-3xl text-white`}></i>
                </div>

                {/* Vendor Name */}
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {vendor.name}
                </h3>

                {/* Description */}
                <p className="text-base text-gray-600">
                  {vendor.description}
                </p>

                {/* Hover Effect Line */}
                <div className={`absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r ${vendor.color} rounded-b-2xl transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300`}></div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Why Choose Us Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gradient-to-br from-teal-600 to-cyan-600 rounded-3xl p-12 lg:p-16 shadow-2xl"
        >
          <div className="text-center mb-12">
            <h3 className="text-4xl font-bold text-white mb-4">
              Why Choose KN-Systems Limited?
            </h3>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Expert consultation and implementation services for enterprise IT infrastructure
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: 'ri-open-source-line', title: 'Open Source', desc: 'Cost-effective solutions' },
              { icon: 'ri-shield-check-line', title: 'Enterprise Security', desc: 'Multi-vendor expertise' },
              { icon: 'ri-customer-service-line', title: '24/7 Support', desc: 'Always available' },
              { icon: 'ri-award-line', title: 'Proven Track Record', desc: 'Trusted by enterprises' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-white/20 backdrop-blur-sm rounded-xl mx-auto mb-4">
                  <i className={`${item.icon} text-3xl text-white`}></i>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                <p className="text-sm text-white/80">{item.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
